import { css, html, nothing, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog.js';
import {
  DEFAULT_TARGET_LANG,
  hashSource,
  LANGUAGE_LABEL,
  sanitizeTranslatedHtml,
  TRANSLATION_LANGUAGES,
  translateHtmlContent,
  translateText,
} from 'src/services/translation';
import { loadStoredTranslation, newStoredTranslation, saveStoredTranslation } from 'src/services/translation-store';
import type { StoredTranslation, TargetLang, TranslatableField } from 'src/services/translation-types';

import 'src/library/elements/d-smooth-resize.js';
import 'src/library/elements/d-action.js';
import 'src/library/fields/d-spinner.js';
import 'src/library/fields/d-view-html.js';
import 'src/library/editors/components/d-edit-html.js';

import type { TranslationDialogInput } from './translation-dialog-input';
import type { TranslationDialogResult } from './translation-dialog-result';
import type { DialogActionInput } from '../library/components/DialogActionInput';

/** A language shown in the dialog: the Norwegian original, or a translation target. */
type DisplayLang = 'no' | TargetLang;

/** Languages shown in the switcher, in display order. Norwegian is read-only. */
const DISPLAY_LANGUAGES: DisplayLang[] = ['no', ...TRANSLATION_LANGUAGES];

const LAST_LANG_KEY = 'translationDialog.lastLang';

function displayLabel(lang: DisplayLang): string {
  return lang === 'no' ? t('translation.norwegian') : LANGUAGE_LABEL[lang];
}

function readLastLang(): TargetLang | undefined {
  try {
    const value = localStorage.getItem(LAST_LANG_KEY);
    return value && (TRANSLATION_LANGUAGES as string[]).includes(value) ? (value as TargetLang) : undefined;
  } catch {
    return undefined;
  }
}

function writeLastLang(lang: TargetLang): void {
  try {
    localStorage.setItem(LAST_LANG_KEY, lang);
  } catch {
    // Ignore storage failures (e.g. private mode).
  }
}

/**
 * Shows an AI translation of an entity's text and HTML fields in a chosen language.
 *
 * Norwegian is the authoritative source. The user (and management) can switch
 * between the Norwegian original and each translation to compare them; the last
 * chosen translation language is remembered. Translations and manual edits are
 * stored in the shared, org-wide translation store, so the whole organization
 * sees the same result.
 */
@customElement('translation-dialog')
export class TranslationDialog extends BaseDialog<TranslationDialogInput, TranslationDialogResult> {
  static readonly styles = BaseDialog.styles.concat(css`
    .lang-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
    }

    button.lang {
      font-family: var(--mainSans);
      font-size: 15px;
      padding: 6px 14px;
      border: 1px solid var(--borderColorTransparent, hsla(0, 0%, 0%, 0.2));
      border-radius: 16px;
      background: white;
      color: var(--linkColorGray, hsl(0, 0%, 30%));
      cursor: pointer;
    }

    button.lang.selected {
      background: hsl(196, 83%, 50%);
      border-color: hsl(196, 83%, 50%);
      color: white;
    }

    button.lang:focus-visible {
      outline: 2px solid hsl(196, 83%, 50%);
      outline-offset: -2px;
    }

    button.lang:focus:not(:focus-visible) {
      outline: none;
    }

    .banner {
      padding: 8px 12px;
      border-radius: 6px;
      margin-bottom: 16px;
      font-size: 14px;
      line-height: 1.4;
    }

    .banner.info {
      background: hsl(48, 100%, 92%);
    }

    .banner.error {
      background: hsl(0, 75%, 94%);
      color: hsl(0, 60%, 35%);
    }

    .loading {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 24px 0;
      color: var(--linkColorGray, hsl(0, 0%, 30%));
    }

    .field {
      margin-bottom: 18px;
    }

    .field-label {
      font-family: var(--mainSans);
      font-size: 13px;
      font-weight: 600;
      color: var(--linkColorGray, hsl(0, 0%, 35%));
      margin-bottom: 4px;
    }

    .field-value.text {
      white-space: pre-wrap;
    }

    textarea.field-edit {
      width: 100%;
      box-sizing: border-box;
      min-height: 60px;
      font-family: var(--mainSans);
      font-size: 16px;
      padding: 8px;
      border: 1px solid var(--borderColor, hsla(0, 0%, 0%, 0.2));
      border-radius: 4px;
      resize: vertical;
    }

    .edit-actions {
      display: flex;
      gap: 16px;
      margin-top: 8px;
    }
  `);

  private organizationId = '';
  private sourceType = '';
  private sourceUuid = '';
  private sourceLabel = '';
  private fields: TranslatableField[] = [];
  private canEdit = false;

  @state() private currentLang: DisplayLang = DEFAULT_TARGET_LANG;
  @state() private loading = false;
  @state() private saving = false;
  @state() private editing = false;
  @state() private error = '';
  @state() private entries: Partial<Record<TargetLang, StoredTranslation>> = {};
  @state() private editValues: Record<string, string> = {};

  protected get calculatedTitle(): string {
    return t('translation.dialogTitle');
  }

  protected get calculatedSubtitle(): string {
    return this.sourceLabel;
  }

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [{ name: t('common:close'), action: 'close' }];
  }

  protected initializeDialog(input: TranslationDialogInput): void {
    this.organizationId = input.organizationId;
    this.sourceType = input.sourceType;
    this.sourceUuid = input.sourceUuid;
    this.sourceLabel = input.sourceLabel;
    this.fields = input.fields;
    this.canEdit = input.canEdit;
    this.currentLang = input.initialLang ?? readLastLang() ?? DEFAULT_TARGET_LANG;
    this.width = 760;
  }

  protected fetchResult(): TranslationDialogResult {
    return { action: 'close' };
  }

  protected firstUpdated(changed: PropertyValues): void {
    super.firstUpdated(changed);
    if (this.currentLang !== 'no') {
      void this.ensureLanguage(this.currentLang);
    }
  }

  async showModal(): Promise<void> {
    await super.showModal();
    // The native dialog auto-focuses the first language button on open, which
    // paints a focus ring on the wrong language (and it gets clipped by the
    // scroll edge). Clear it so a ring only appears for keyboard navigation.
    (this.shadowRoot?.activeElement as HTMLElement | null)?.blur();
  }

  private get currentHash(): string {
    return hashSource(...this.fields.map((f) => `${f.key}=${f.value}`));
  }

  private async ensureLanguage(lang: TargetLang): Promise<void> {
    if (this.entries[lang]) return;
    this.loading = true;
    this.error = '';
    try {
      const stored = await loadStoredTranslation(
        this.organizationId,
        this.sourceType,
        this.sourceUuid,
        lang,
        this.currentHash,
      );
      if (stored) {
        this.entries = { ...this.entries, [lang]: stored };
      } else {
        await this.machineTranslate(lang);
      }
    } catch {
      this.error = t('translation.errorLoad');
    } finally {
      this.loading = false;
    }
  }

  private async machineTranslate(lang: TargetLang): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const translated = await Promise.all(
        this.fields.map(
          async (f) =>
            [
              f.key,
              f.type === 'html'
                ? await translateHtmlContent(this.organizationId, f.value, lang)
                : await translateText(this.organizationId, f.value, lang),
            ] as const,
        ),
      );
      const translation = newStoredTranslation({
        sourceType: this.sourceType,
        sourceUuid: this.sourceUuid,
        lang,
        fields: Object.fromEntries(translated),
        sourceHash: this.currentHash,
        edited: false,
        machineTranslated: true,
        model: 'chat-gpt',
      });
      // Persist immediately so the whole organization shares it. Ignore failures
      // (e.g. read-only users) and keep the translation for this session.
      await saveStoredTranslation(this.organizationId, translation).catch(() => undefined);
      this.entries = { ...this.entries, [lang]: translation };
    } catch {
      this.error = t('translation.errorTranslate');
    } finally {
      this.loading = false;
    }
  }

  private async switchLanguage(lang: DisplayLang): Promise<void> {
    if (lang === this.currentLang) return;
    this.editing = false;
    this.currentLang = lang;
    if (lang !== 'no') {
      writeLastLang(lang);
      await this.ensureLanguage(lang);
    }
  }

  private startEdit(): void {
    const lang = this.currentLang;
    if (lang === 'no') return;
    const translation = this.entries[lang];
    if (!translation) return;
    this.editValues = Object.fromEntries(this.fields.map((f) => [f.key, translation.fields[f.key] ?? '']));
    this.editing = true;
  }

  private cancelEdit(): void {
    this.editing = false;
  }

  private async saveEdit(): Promise<void> {
    const lang = this.currentLang;
    if (lang === 'no') return;
    this.saving = true;
    this.error = '';
    try {
      const fields = Object.fromEntries(
        this.fields.map((f) => {
          const value = this.editValues[f.key] ?? '';
          return [f.key, f.type === 'html' ? sanitizeTranslatedHtml(value) : value.trim()];
        }),
      );
      const translation = newStoredTranslation({
        sourceType: this.sourceType,
        sourceUuid: this.sourceUuid,
        lang,
        fields,
        sourceHash: this.currentHash,
        edited: true,
        machineTranslated: false,
      });
      await saveStoredTranslation(this.organizationId, translation);
      this.entries = { ...this.entries, [lang]: translation };
      this.editing = false;
    } catch {
      this.error = t('translation.errorSave');
    } finally {
      this.saving = false;
    }
  }

  private async retranslate(): Promise<void> {
    const lang = this.currentLang;
    if (lang === 'no') return;
    this.editing = false;
    await this.machineTranslate(lang);
  }

  renderBody() {
    const lang = this.currentLang;
    return html`
      <d-smooth-resize>
        <div class="lang-bar">
          ${DISPLAY_LANGUAGES.map(
            (l) =>
              html`<button class="lang ${l === lang ? 'selected' : ''}" @click=${() => this.switchLanguage(l)}>
                ${displayLabel(l)}
              </button>`,
          )}
        </div>
        ${this.error ? html`<div class="banner error">${this.error}</div>` : nothing}
        ${lang === 'no'
          ? this.renderNorwegian()
          : this.loading
            ? html`<div class="loading"><d-spinner></d-spinner><span>${t('translation.translating')}</span></div>`
            : this.editing
              ? this.renderEdit()
              : this.renderView(this.entries[lang])}
      </d-smooth-resize>
    `;
  }

  private renderNorwegian() {
    return html`
      <div class="banner info">${t('translation.norwegianOriginal')}</div>
      ${this.fields.map((f) => this.renderFieldValue(f, f.value))}
    `;
  }

  private renderView(translation?: StoredTranslation) {
    if (!translation) return nothing;
    return html`
      ${this.renderStatusBanner(translation)}
      ${this.fields.map((f) => this.renderFieldValue(f, translation.fields[f.key] ?? ''))}
      ${this.canEdit
        ? html`<div class="edit-actions">
            <d-action @click=${() => this.startEdit()}>${t('content.edit')}</d-action>
            <d-action @click=${() => this.retranslate()}>${t('translation.retranslate')}</d-action>
          </div>`
        : nothing}
    `;
  }

  private renderFieldValue(field: TranslatableField, value: string) {
    return html`
      <div class="field">
        <div class="field-label">${field.label}</div>
        ${field.type === 'html'
          ? html`<d-view-html .value=${value}></d-view-html>`
          : html`<div class="field-value text">${value}</div>`}
      </div>
    `;
  }

  private renderStatusBanner(translation: StoredTranslation) {
    if (translation.edited) {
      return html`<div class="banner info">${t('translation.editedManually')}</div>`;
    }
    if (translation.machineTranslated) {
      return html`<div class="banner info">${t('translation.machineTranslatedNotice')}</div>`;
    }
    return nothing;
  }

  private renderEdit() {
    return html`
      ${this.fields.map(
        (f) => html`
          <div class="field">
            <div class="field-label">${f.label}</div>
            ${f.type === 'html'
              ? html`<d-edit-html
                  field=""
                  .value=${this.editValues[f.key] ?? ''}
                  .docsForLinking=${[]}
                  @value-changed=${(e: CustomEvent<{ value: string }>) => this.onEditValue(f.key, e.detail.value)}
                ></d-edit-html>`
              : html`<textarea
                  class="field-edit"
                  .value=${this.editValues[f.key] ?? ''}
                  @input=${(e: Event) => this.onEditValue(f.key, (e.target as HTMLTextAreaElement).value)}
                ></textarea>`}
          </div>
        `,
      )}
      <div class="edit-actions">
        <d-action ?disabled=${this.saving} @click=${() => this.cancelEdit()}>${t('common:cancel')}</d-action>
        <d-action button ?disabled=${this.saving} @click=${() => this.saveEdit()}>${t('common:save')}</d-action>
      </div>
    `;
  }

  private onEditValue(key: string, value: string): void {
    this.editValues = { ...this.editValues, [key]: value };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'translation-dialog': TranslationDialog;
  }
}
